import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'store/redux.store'
import { DataHub, GROUP } from 'src/utils/datahub'
import { ACTION, CATEGORY, DataLayer, EVENT, GoogleAnalyticsDataLayer } from 'src/utils/google-analytics'
import { ReactComponent as ToolsSVG } from '../../assets/menu-tools.svg'
import { useResponsiveHotDropdown } from '../../hooks/use-responsive-hot-dropdown'
import { useToggleHotDropdown } from '../../hooks/use-toggle-hot-dropdown'

import { menuItems } from './dynamic.data'

import { MenuItem, MENU_GROUP_COLORS } from './dynamic.types'
import MenuDynamicItem from './dynamic-item.component'

import './dynamic.styles.scss'

const MenuDynamic = ({ tabIndex }) => {
  const { user, context } = useSelector(state => state)
  const history = useHistory()
  const { t } = useTranslation()
  const hotDropdownRef = useRef<JSX.IntrinsicElements['hot-dropdown']>(null)
  let toolTabIndex = 0
  const getNextToolTabIndex = () => (toolTabIndex += 1)

  const { toggle } = useToggleHotDropdown(hotDropdownRef)

  useResponsiveHotDropdown(hotDropdownRef, { maxHeight: 603 })

  const toolsMenuClickHandler = () => {
    const dataLayer = new DataLayer(EVENT.INTERACTION, CATEGORY.UNIVERSAL_BAR, ACTION.CLICK_MENU_TOOLS, 'tools')

    GoogleAnalyticsDataLayer.sendPageInteraction(dataLayer)
  }

  type ToolClickHandler = Pick<MenuItem, 'route' | 'dataLayerEvents' | 'dataHubEvents'>
  const toolClickHandler = async ({ route, dataLayerEvents, dataHubEvents }: ToolClickHandler) => {
    dataLayerEvents.forEach(GoogleAnalyticsDataLayer.sendPageInteraction)
    dataHubEvents.forEach(event => {
      DataHub.sendDHMetrifyEvent({
        user: { id: user?.id },
        path: window.location.pathname,
        queryParams: window.location.search,
        params: { tab: 'producer' },
        ...event
      })
    })

    if (typeof route === 'function') {
      await route()

      return
    }

    if (route.indexOf('http://') === 0 || route.indexOf('https://') === 0) {
      const target = route === process.env.URL_EXTENSIONS ? '_self' : '_blank'

      window.open(route, target)
      return
    }

    history.push(route)
  }

  function handleClickAllTools() {
    const dataLayerEvents: DataLayer[] = [
      new DataLayer(EVENT.INTERACTION, CATEGORY.UNIVERSAL_BAR, ACTION.CLICK_MENU_TOOLS, 'SeeAllTools')
    ]
    const dataHubEvents = [
      {
        name: 'AllTools',
        group: GROUP.UNIVERSALBAR_TOOLS
      }
    ]

    toolClickHandler({
      route: '/tools',
      dataLayerEvents,
      dataHubEvents
    })
  }

  function handleMenuKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      toggle()
    }
  }

  function filterExistentItems(items?: string[]): string[] {
    return items?.filter(item => menuItems[item]) || []
  }

  return (
    context && (
      <div slot="header-actions">
        <hot-dropdown
          tabIndex={tabIndex}
          onKeyDown={handleMenuKeyDown}
          ref={hotDropdownRef}
          class="header-tools hot-dropdocked"
        >
          <hot-menu-item onClick={toolsMenuClickHandler} slot="button" class="_cursor-pointer">
            <div className="hot-application-menu__icon">
              <hot-tooltip content={t('header.menu.general.browse')} position="bottom" class="_d-flex">
                <ToolsSVG />
              </hot-tooltip>
            </div>
          </hot-menu-item>
          <div slot="menu" className="_text-gray-600" id="menu-dynamic-items">
            <div className="_p-4">
              {context.dynamicMenu.groups?.map(({ title, items = [] }, index) => (
                <div key={title} id={title}>
                  <p className="_mb-2 _pl-4 _text-black">{t(`header.menu.general.${title}`)}</p>
                  <div className="header-tools__items">
                    {filterExistentItems(items).map(item => (
                      <MenuDynamicItem
                        key={item}
                        title={t(menuItems[item].title)}
                        icon={menuItems[item].icon}
                        onClickHandler={() => toolClickHandler(menuItems[item])}
                        bgColor={MENU_GROUP_COLORS[title]}
                        tabIndex={getNextToolTabIndex()}
                        testid={menuItems[item].testid}
                        isBeta={menuItems[item]?.isBeta}
                      />
                    ))}
                  </div>

                  {index !== context.dynamicMenu.groups.length - 1 && <hr className="_my-3 _border-gray-200" />}
                </div>
              ))}
            </div>

            {context.dynamicMenu.showAllToolsLink && (
              <div className="header-tools__all-tools">
                <button data-testid="see-all-tools" onClick={handleClickAllTools}>
                  {t('header.menu.dynamic_menu.see_all_tools')}
                </button>
              </div>
            )}
          </div>
        </hot-dropdown>
      </div>
    )
  )
}

export default React.memo(MenuDynamic)
